/**
 * Internal Auditor Page Review
 *
 * See docs/COMPLIANCE_LIFECYCLE_PLAN.md §3 Phase D.
 *
 * Shows all compliance pages currently in `submitted` status for the internal
 * auditor's company. Per-page actions:
 *   - Approve → transitions page to `approved_for_audit` (the company admin
 *     can then request an external audit)
 *   - Request Changes → transitions page back to `in_progress` with a reason;
 *     compliance user fixes and resubmits
 *
 * This closes the gap between evidence-level review (EvidenceReview.tsx) and
 * the external auditor dashboard. Without it, submitted pages sit forever in
 * `submitted` status with no one to move them forward.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, FileText, User, Clock, RefreshCw, ChevronDown, ChevronRight, Download, ExternalLink, Paperclip, X } from 'lucide-react';
import { notify } from '@/core/notifications/notify';
import { useModal, ConfirmModal } from '@components/modal';

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface EvidenceItem {
  id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  status: string;
  created_at: string;
}

interface SubmittedPage {
  id: number;
  framework_id: number;
  framework_version_id: number;
  framework_name: string;
  framework_version: string;
  section: string;
  title: string;
  description?: string;
  status: string;
  submitted_at: string;
  due_date?: string | null;
  assigned_first_name?: string;
  assigned_last_name?: string;
  assigned_email?: string;
  evidence_count: number;
  verified_count: number;
  evidence: EvidenceItem[];
}

const PageReview: React.FC = () => {
  const { openModal } = useModal();
  const [pages, setPages] = useState<SubmittedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingOnPageId, setActingOnPageId] = useState<number | null>(null);
  const [expandedPageIds, setExpandedPageIds] = useState<Set<number>>(new Set());
  // Inline "Request Changes" reason modal state
  const [requestChangesFor, setRequestChangesFor] = useState<{ pageId: number; title: string } | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [requestingChanges, setRequestingChanges] = useState(false);

  const toggleExpanded = (pageId: number) => {
    setExpandedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const downloadEvidence = async (evidenceId: number, fileName: string) => {
    try {
      const res = await fetch(`/api/compliance/execution/evidence/${evidenceId}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) {
        notify.error('Failed to download evidence');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      notify.error(e?.message ?? 'Download failed');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/compliance/execution/company/pages/submitted', {
        headers: authHeaders(),
      });
      const j = await res.json();
      if (j.success) {
        setPages(j.data ?? []);
      } else {
        notify.error(j.message ?? 'Failed to load submitted pages');
      }
    } catch (e: any) {
      notify.error(e?.message ?? 'Failed to load submitted pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = (pageId: number, title: string) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Approve for External Audit',
        message: `Approve "${title}" for external audit? The compliance page and all its evidence will be marked verified. The company admin will then be able to request the external audit.`,
        confirmText: 'Approve',
        cancelText: 'Cancel',
        type: 'default',
        onConfirm: async () => {
          setActingOnPageId(pageId);
          try {
            const res = await fetch(`/api/compliance/execution/company/pages/${pageId}/approve`, {
              method: 'POST',
              headers: authHeaders(),
            });
            const j = await res.json();
            if (!j.success) {
              notify.error(j.message ?? 'Approve failed');
              return;
            }
            notify.success(`"${title}" approved. Company admin can now request external audit.`);
            setPages((prev) => prev.filter((p) => p.id !== pageId));
          } catch (e: any) {
            notify.error(e?.message ?? 'Approve failed');
          } finally {
            setActingOnPageId(null);
          }
        },
        onCancel: () => {},
      },
    });
  };

  const openRequestChanges = (pageId: number, title: string) => {
    setChangeReason('');
    setRequestChangesFor({ pageId, title });
  };

  const submitRequestChanges = async () => {
    if (!requestChangesFor) return;
    const reason = changeReason.trim();
    if (reason.length < 5) {
      notify.error('Reason must be at least 5 characters so the compliance user knows what to fix.');
      return;
    }
    setRequestingChanges(true);
    setActingOnPageId(requestChangesFor.pageId);
    try {
      const res = await fetch(`/api/compliance/execution/company/pages/${requestChangesFor.pageId}/request-changes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ comments: reason }),
      });
      const j = await res.json();
      if (!j.success) {
        notify.error(j.message ?? 'Request changes failed');
        return;
      }
      notify.success(`Changes requested on "${requestChangesFor.title}". Compliance user will see the reason.`);
      setPages((prev) => prev.filter((p) => p.id !== requestChangesFor.pageId));
      setRequestChangesFor(null);
      setChangeReason('');
    } catch (e: any) {
      notify.error(e?.message ?? 'Request changes failed');
    } finally {
      setRequestingChanges(false);
      setActingOnPageId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Pending Internal Review</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Compliance pages submitted by your team — review and approve for external audit, or send back for changes.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
            Loading submitted pages…
          </div>
        ) : pages.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">Nothing pending</p>
            <p className="text-xs text-slate-500 mt-1">
              No pages are currently awaiting your review. When compliance users submit their pages, they'll appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pages.map((p) => {
              const submittedBy = p.assigned_first_name
                ? `${p.assigned_first_name} ${p.assigned_last_name ?? ''}`.trim()
                : 'Unknown';
              const isActing = actingOnPageId === p.id;
              const isExpanded = expandedPageIds.has(p.id);
              const evidenceFiles = p.evidence ?? [];
              return (
                <div key={p.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText size={14} className="text-slate-400 flex-shrink-0" />
                          <p className="text-xs text-slate-500 truncate text-left">
                            <span className="font-semibold">{p.framework_name}</span> v{p.framework_version} · {p.section}
                          </p>
                        </div>
                        <h3 className="text-base font-bold text-slate-800 text-left">{p.title}</h3>
                        {p.description && (
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2 text-left">{p.description}</p>
                        )}
                        {/* Evidence status summary — shows exactly what's in each status so the auditor
                            can decide between Approve and Request Changes at a glance. */}
                        {(() => {
                          const verifiedCount = evidenceFiles.filter((e) => e.status === 'INTERNAL_VERIFIED').length;
                          const rejectedCount = evidenceFiles.filter((e) => e.status === 'REJECTED').length;
                          const pendingCount = evidenceFiles.filter((e) => e.status === 'submitted' || e.status === 'pending_review' || e.status === 'draft').length;
                          return (
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-500">
                              <span className="flex items-center gap-1">
                                <User size={11} /> {submittedBy}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={11} /> Submitted {new Date(p.submitted_at).toLocaleString()}
                              </span>
                              {p.due_date && (() => {
                                const overdueAtSubmit = new Date(p.submitted_at) > new Date(p.due_date);
                                return (
                                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${overdueAtSubmit ? 'text-red-700 bg-red-50 font-semibold' : 'text-slate-500 bg-slate-50'}`}>
                                    Due {new Date(p.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {overdueAtSubmit && ' — submitted late'}
                                  </span>
                                );
                              })()}
                              {rejectedCount > 0 ? (
                                <span className="flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded font-semibold">
                                  <XCircle size={11} /> {rejectedCount} file{rejectedCount === 1 ? '' : 's'} rejected — use Request Changes
                                </span>
                              ) : verifiedCount === evidenceFiles.length && evidenceFiles.length > 0 ? (
                                <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-semibold">
                                  <CheckCircle2 size={11} /> {verifiedCount}/{evidenceFiles.length} evidence verified — ready to approve
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Paperclip size={11} /> {evidenceFiles.length} file{evidenceFiles.length === 1 ? '' : 's'} attached
                                  {verifiedCount > 0 && ` · ${verifiedCount} verified`}
                                  {pendingCount > 0 && ` · ${pendingCount} pending`}
                                </span>
                              )}
                              <button
                                onClick={() => toggleExpanded(p.id)}
                                className="flex items-center gap-0.5 text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                {isExpanded ? 'Hide evidence' : 'View evidence'}
                              </button>
                              <Link
                                to={`/compliance/pages/${p.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-0.5 text-blue-600 hover:text-blue-800 font-medium"
                              >
                                <ExternalLink size={11} /> Open full page
                              </Link>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {(() => {
                          const hasRejected = evidenceFiles.some((e) => e.status === 'REJECTED');
                          const allVerified = evidenceFiles.length > 0 && evidenceFiles.every((e) => e.status === 'INTERNAL_VERIFIED');
                          const approveDisabled = isActing || hasRejected;
                          return (
                            <>
                              {/* Request Changes:
                                    - hasRejected → prominent solid amber (primary action)
                                    - allVerified → subtle text link (rare page-level concern only)
                                    - otherwise → soft amber pill (equal to Approve) */}
                              {hasRejected ? (
                                <button
                                  onClick={() => openRequestChanges(p.id, p.title)}
                                  disabled={isActing}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 text-white bg-amber-600 hover:bg-amber-700 border-amber-600"
                                >
                                  <XCircle size={13} /> Request Changes
                                </button>
                              ) : allVerified ? (
                                <button
                                  onClick={() => openRequestChanges(p.id, p.title)}
                                  disabled={isActing}
                                  className="text-xs text-slate-500 hover:text-amber-700 underline underline-offset-2 disabled:opacity-50"
                                  title="Rare: use this only for a page-level concern unrelated to the evidence (e.g. section mapping error, missing description)"
                                >
                                  Request Changes
                                </button>
                              ) : (
                                <button
                                  onClick={() => openRequestChanges(p.id, p.title)}
                                  disabled={isActing}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200"
                                >
                                  <XCircle size={13} /> Request Changes
                                </button>
                              )}
                              <button
                                onClick={() => approve(p.id, p.title)}
                                disabled={approveDisabled}
                                title={
                                  hasRejected
                                    ? 'Cannot approve — this page has rejected evidence. Use Request Changes instead so the compliance user can replace the file.'
                                    : 'Approve this page for external audit'
                                }
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                  approveDisabled
                                    ? 'text-slate-400 bg-slate-200 cursor-not-allowed'
                                    : 'text-white bg-emerald-600 hover:bg-emerald-700'
                                }`}
                              >
                                <CheckCircle2 size={13} /> Approve for External Audit
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-200 bg-slate-50/60 px-5 py-4">
                      <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 text-left">
                        Evidence files ({evidenceFiles.length})
                      </p>
                      {evidenceFiles.length === 0 ? (
                        <p className="text-xs text-slate-500 italic text-left">No evidence files attached.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {evidenceFiles.map((ev) => (
                            <li
                              key={ev.id}
                              className="flex items-center justify-between gap-3 bg-white rounded-md border border-slate-200 px-3 py-2"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileText size={13} className="text-slate-400 flex-shrink-0" />
                                <span className="text-xs text-slate-700 truncate text-left">{ev.file_name}</span>
                                <span className="text-[10px] text-slate-400 flex-shrink-0">
                                  {(ev.file_size / 1024).toFixed(1)} KB
                                </span>
                              </div>
                              <button
                                onClick={() => downloadEvidence(ev.id, ev.file_name)}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                              >
                                <Download size={12} /> Download
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="text-[10px] text-slate-400 mt-3 italic text-left">
                        Tip: Approving this page auto-verifies all evidence files above.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request Changes reason modal — replaces the old window.prompt() */}
      {requestChangesFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full mx-4 shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Request Changes</h2>
                <button
                  onClick={() => {
                    setRequestChangesFor(null);
                    setChangeReason('');
                  }}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-slate-600 mb-4 text-left">
                Tell the compliance user what to fix on <strong>"{requestChangesFor.title}"</strong>. They will see your reason and resubmit the page after making changes.
              </p>
              <label className="block text-xs font-medium text-slate-500 mb-1 text-left">Reason (min 5 characters) *</label>
              <textarea
                autoFocus
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="e.g. The uploaded policy is out of date — please attach the 2025 revision signed by the CISO."
                className="w-full h-32 p-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
              />
              <div className="flex items-center justify-end gap-3 mt-5">
                <button
                  onClick={() => {
                    setRequestChangesFor(null);
                    setChangeReason('');
                  }}
                  disabled={requestingChanges}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRequestChanges}
                  disabled={requestingChanges || changeReason.trim().length < 5}
                  className="px-4 py-2 text-sm font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {requestingChanges ? 'Sending…' : 'Send Back for Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageReview;
